import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { tokenStorage } from '../utils/storage';
import { compressImage } from '../utils/imageHelpers';

const isNative = Capacitor.isNativePlatform();
const useHosted = import.meta.env.VITE_USE_HOSTED_API === 'true';

// export const imageRoot = isNative || window.location.hostname !== 'localhost' || useHosted
//     ? 'https://shopaxo.in/API'
//     : '';

// const api = axios.create({ 
//     baseURL: isNative || window.location.hostname !== 'localhost' || useHosted
//         ? 'https://shopaxo.in/API/api'
//         : '/api',
//   timeout: 120000,
// });


export const imageRoot = isNative || window.location.hostname !== 'localhost' || useHosted
    ? 'https://shopaxo.in/API'
    : 'http://localhost:5000';

const api = axios.create({
    baseURL: isNative || window.location.hostname !== 'localhost' || useHosted
        ? 'https://shopaxo.in/API/api'
        : 'http://localhost:5000/api',
    timeout: 120000,
});



let onUnauthorizedCallback = null;
export const setOnUnauthorized = (callback) => {
  onUnauthorizedCallback = callback;
};

// Attach JWT token to every request and compress image uploads
api.interceptors.request.use(async (config) => {
  const token = await tokenStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Intercept and compress images inside FormData
  if (config.data instanceof FormData) {
    try {
      const newFd = new FormData();
      for (const [key, value] of config.data.entries()) {
        if (value instanceof File && value.type?.startsWith('image/')) {
          const compressed = await compressImage(value);
          newFd.append(key, compressed);
        } else {
          newFd.append(key, value);
        }
      }
      config.data = newFd;
    } catch (e) {
      console.error('Failed to compress image in Axios request interceptor:', e);
    }
  }

  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Auto refresh token on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401) {
      if (!original._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              original.headers.Authorization = `Bearer ${token}`;
              return api(original);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        original._retry = true;
        isRefreshing = true;

        const refreshToken = await tokenStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const res = await axios.post(
              isNative || window.location.hostname !== 'localhost' || useHosted
                ? 'https://shopaxo.in/API/api/auth/refresh'
                : '/api/auth/refresh', 
              { refreshToken }
            );
            const { accessToken, refreshToken: newRefresh } = res.data.data;
            await tokenStorage.setItem('accessToken', accessToken);
            await tokenStorage.setItem('refreshToken', newRefresh);
            original.headers.Authorization = `Bearer ${accessToken}`;
            processQueue(null, accessToken);
            isRefreshing = false;
            return api(original);
          } catch (err) {
            processQueue(err, null);
            isRefreshing = false;
            // Failed to refresh token
          }
        }
      }
      await tokenStorage.clear();
      if (onUnauthorizedCallback) {
        onUnauthorizedCallback();
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  sendOtp: (mobile) => api.post('/auth/send-otp', { mobile }),
  verifyOtp: (mobile, code) => api.post('/auth/verify-otp', { mobile, code }),
  sendEmailOtp: (email) => api.post('/auth/send-email-otp', { email }),
  verifyEmailOtp: (email, code) => api.post('/auth/verify-email-otp', { email, code }),
  logout: () => api.post('/auth/logout'),
  registerFcmToken: (token) => api.post('/auth/register-fcm-token', { token }),
  registerDevice: (data) => api.post('/mobile/register-device', data),
  unregisterDevice: (data) => api.post('/mobile/unregister-device', data),
  uploadProfilePic: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/auth/profile-pic', fd);
  },
};

// Shops
export const shopApi = {
    create: (data) => api.post('/shops', data),
    get: (slug) => api.get(`/shops/${slug}`),
    getById: (id) => api.get(`/shops/id/${id}`),
    update: (id, data) => api.post(`/shops/${id}/update`, data),
    search: (params) => api.get('/shops/search', { params }),
    myShops: () => api.get('/shops/my-shops'),
    follow: (id) => api.post(`/shops/${id}/follow`),
    unfollow: (id) => api.delete(`/shops/${id}/follow`),
    uploadLogo: (id, file) => {
        const fd = new FormData(); fd.append('file', file);
        return api.post(`/shops/${id}/logo`, fd);
    },
    uploadBanner: (id, file) => {
        const fd = new FormData(); fd.append('file', file);
        return api.post(`/shops/${id}/banner`, fd);
    },
    subscribe: (id, data) => api.post(`/shops/${id}/subscribe`, data),
    getCustomers: (shopId) => api.get(`/shops/${shopId}/customers`),
    createCustomer: (shopId, data) => api.post(`/shops/${shopId}/customers`, data),
    getCustomerLedger: (shopId, customerId) => api.get(`/shops/${shopId}/customers/${customerId}/ledger`),
    // recordPayment: (shopId, customerId, data) => api.post(`/shops/${shopId}/customers/${customerId}/payments`, data),

    recordPayment: (data) => api.post(`/shops/payments`, data),
    getMyLedger: (shopId) => api.get(`/shops/${shopId}/my-ledger`),
    getMySummary: (shopId) => api.get(`/shops/${shopId}/my-summary`),
};

// Products
export const productApi = {
    create: (shopId, data) => api.post(`/products/shop/${shopId}`, data),
    get: (id) => api.get(`/products/${id}`),
    getShopProducts: (shopId, params) => api.get(`/products/shop/${shopId}`, { params }),
    getFeed: (params) => api.get('/products/feed', { params }),
    update: (id, data) => api.post(`/products/${id}/update`, data),
    delete: (id) => api.delete(`/products/${id}`),
    clearMedia: (id) => api.delete(`/products/${id}/media`),
    uploadMedia: (id, files) => {
        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        return api.post(`/products/${id}/media`, fd);
    },
    analyze: (id, imageUrl) => api.post(`/products/${id}/analyze`, { imageUrl }),
    analyzeUpload: (file) => {
        const fd = new FormData(); fd.append('file', file);
        return api.post('/products/analyze-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    getFavourites: () => api.get('/products/favourites'),
    addFavourite: (id) => api.post(`/products/${id}/favourite`),
    removeFavourite: (id) => api.delete(`/products/${id}/favourite`),
    aiSearchShop: (shopId, query) => api.post(`/products/shop/${shopId}/ai-search`, { query }),
    marketSearch: (query) => api.post('/products/market-search', { query }),
    publish: (id) => api.post(`/products/${id}/publish`),
    broadcast: (id) => api.post(`/products/${id}/broadcast`),
    adjustStock: (id, adjustment) => api.post(`/products/${id}/adjust-stock`, { adjustment }),
    getProductComments: (productId) => api.get(`/products/${productId}/comments`),
    addProductComment: (productId, data) => api.post(`/products/${productId}/comments`, data),
};

// Orders
export const orderApi = {
    create: (data) => api.post('/orders', data),
    update: (data) => api.post('/orders/update', data),
    pay: (id, data) => api.post(`/orders/${id}/pay`, data),
    payAndClose: (id) => api.post(`/orders/${id}/pay-and-close`),
    get: (id) => api.get(`/orders/${id}`),
    getShopOrders: (shopId, params) => api.get(`/orders/shop/${shopId}`, { params }),
    getMyOrders: (params) => api.get('/orders/my-orders', { params }),

    updateStatus: (data) => api.post('/orders/status', data),

    addItem: (id, item) => api.post(`/orders/${id}/items`, item),

    // Changed DELETE -> POST
    removeItem: (id, itemId) =>
        api.post(`/orders/${id}/items/${itemId}/delete`),

    negotiate: (id, data) =>
        api.post(`/orders/${id}/negotiate`, data),

    // Changed PUT -> POST
    respondNegotiation: (id, accept) =>
        api.post(`/orders/negotiations/${id}/respond`, { accept }),

    freeze: (data) =>
        api.post('/orders/status', data),

    dispatch: (id) =>
        api.post('/orders/status', {
            orderId: id,
            status: 'Dispatched'
        }),

    close: (id) =>
        api.post('/orders/status', {
            orderId: id,
            status: 'Closed'
        }),

    cancel: (id) =>
        api.post('/orders/status', {
            orderId: id,
            status: 'Cancelled'
        }),
};

// Parchis
export const parchiApi = {
    upload: (shopId, image) => {
        const fd = new FormData();
        fd.append('shopId', shopId);
        fd.append('image', image);
        return api.post('/parchis/upload', fd);
    },
    get: (id) => api.get(`/parchis/${id}`),
    update: (id, data) => api.post(`/parchis/${id}/update`, data),
    getShopParchis: (shopId, q) => api.get(`/parchis/shop/${shopId}`, { params: { q } }),
    postLedger: (id, data) => api.post(`/parchis/${id}/post-ledger`, data),
};

// Categories
export const categoryApi = {
  getAll: () => api.get('/categories'),
};

// Conversations/Chats
export const chatApi = {
    searchUser: (mobile) => api.get(`/conversations/search-user`, { params: { mobile } }),
    getParticipants: (id) => api.get(`/conversations/${id}/participants`),
    addParticipant: (id, userId) => api.post(`/conversations/${id}/participants`, { userId }),
    editMessage: (id, content) => api.post(`/conversations/messages/${id}/edit`, { content }),
    deleteMessage: (id) => api.delete(`/conversations/messages/${id}`),
    reactMessage: (id, emoji) => api.post(`/conversations/messages/${id}/react`, { emoji }),
    forwardMessage: (id, targetConversationIds) => api.post(`/conversations/messages/${id}/forward`, { targetConversationIds }),
};

// Broadcasts
export const broadcastApi = {
  getLists: (shopId) => api.get(`/broadcasts/shop/${shopId}`),
  createList: (data) => api.post('/broadcasts', data),
  deleteList: (id) => api.delete(`/broadcasts/${id}`),
  getMembers: (id) => api.get(`/broadcasts/${id}/members`),
  addMember: (id, customerId) => api.post(`/broadcasts/${id}/members`, { customerId }),
  removeMember: (id, customerId) => api.delete(`/broadcasts/${id}/members/${customerId}`),
  sendMessage: (id, data) => api.post(`/broadcasts/${id}/send`, data),
};

// Notifications
export const notificationApi = {
    getAll: () => api.get('/notifications'),
    unreadCount: () => api.get('/notifications/unread-count'),
    markRead: (id) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post('/notifications/read-all'),
};

// Relationships
export const relationshipApi = {
  getMyRelationships: () => api.get('/relationships'),
  get: (id) => api.get(`/relationships/${id}`),
  getByUserId: (userId) => api.get(`/relationships/by-user/${userId}`),
  getLedger: (id) => api.get(`/relationships/${id}/ledger`),
  rebuild: (id) => api.post(`/relationships/${id}/rebuild`),
};

// Reports & Analytics
export const reportApi = {
  getDashboard: (shopId) => api.get(`/dashboard/shop/${shopId}`),
  getStockSummary: (shopId, params) => api.get(`/dashboard/shop/${shopId}/stock-summary`, { params }),
  getStockLedger: (shopId, productId, params) => api.get(`/dashboard/shop/${shopId}/products/${productId}/stock-ledger`, { params }),
};

// Wishlist
export const wishlistApi = {
    addToWishlist: (productId) => api.post(`/wishlist/${productId}`),
    removeFromWishlist: (productId) => api.delete(`/wishlist/${productId}`),
    getMyWishlist: () => api.get('/wishlist/my'),
    getShopWishlistStats: () => api.get('/wishlist/shop-stats'),
};

// Admin
export const adminApi = {
    getViolations: () => api.get('/admin/violations'),
    reactivateUser: (userId) => api.post(`/admin/violations/reactivate/${userId}`),
    getUsers: () => api.get('/admin/users'),
    toggleUserActive: (userId) => api.post(`/admin/users/${userId}/toggle-active`),
    toggleUserAdmin: (userId) => api.post(`/admin/users/${userId}/toggle-admin`),
    getShops: () => api.get('/admin/shops'),
    toggleShopActive: (shopId) => api.post(`/admin/shops/${shopId}/toggle-active`),
};


